// Editor-free helpers for the serialized `<reference .../>` grammar, so
// consumers do not pull the lazy-loaded mdxeditor bundle into their chunk.

import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import type { BeautifulMentionsItem } from "lexical-beautiful-mentions";

/** `label` is a reserved data key — it collides with the serialized `label` attribute. */
export type MentionItems = BeautifulMentionsItem[];

export type PromptBoxMention = {
  trigger: string;
  value: string;
  data?: Record<string, unknown>;
};

export const mentionKey = (mention: PromptBoxMention): string =>
  typeof mention.data?.id === "string" ? mention.data.id : mention.value;

export type MentionKindConfig = {
  /** Dropdown group header. Omitted: the group renders without a header. */
  label?: string;
  /** Icon for this kind everywhere (dropdown, editor, sent messages, chip rows). */
  icon?: IconDefinition;
  getDescription?: (data: Record<string, unknown>) => string | undefined;
};

export const resolveKind = (
  kinds: MentionKinds | undefined,
  kind: unknown,
): MentionKindConfig | undefined =>
  typeof kind === "string" ? kinds?.[kind] : undefined;

export type MentionKinds = Record<string, MentionKindConfig>;

export const REFERENCE_TAG = "reference";

// Attribute-pair-aware so quoted values may contain `>`.
export const REFERENCE_ATTRS_PATTERN = '(?:\\s+[A-Za-z][\\w-]*="[^"]*")*';

const REFERENCE_TAG_RE = new RegExp(
  `<${REFERENCE_TAG}(${REFERENCE_ATTRS_PATTERN})\\s*/>`,
  "g",
);

const ATTR_RE = /([A-Za-z][\w-]*)="([^"]*)"/g;

// mdast-util-mdx-jsx encodes `"` inside attribute values as `&#x22;`.
const decodeAttrValue = (value: string): string =>
  value.replace(/&#x22;/g, '"');

export const parseReferenceAttributes = (raw: string): Record<string, string> =>
  Object.fromEntries(
    [...raw.matchAll(ATTR_RE)].map((match) => [
      match[1],
      decodeAttrValue(match[2]),
    ]),
  );

/** Drops nullish entries and stringifies the rest for tag serialization. */
export const mentionDataToAttrs = (
  data: Record<string, unknown>,
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(data)
      .filter(([, value]) => value != null)
      .map(([key, value]) => [key, String(value)]),
  );

/** Attribute maps of all `<reference .../>` tags, in document order. */
export const extractReferences = (markdown: string): Record<string, string>[] =>
  [...markdown.matchAll(REFERENCE_TAG_RE)].map((match) =>
    parseReferenceAttributes(match[1] ?? ""),
  );

export const referencesToPlainText = (markdown: string): string =>
  markdown
    .replace(REFERENCE_TAG_RE, (match, rawAttrs) => {
      const label = parseReferenceAttributes(rawAttrs ?? "").label;
      return label ?? match;
    })
    // mdxJsxToMarkdown escapes plain-text `<` as `\<`.
    .replace(/\\</g, "<");
